import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { PermissionService } from '../auth/permission.service';
import { FeatureAttribute } from '@app/data/models/common/feature.model';
import { DatabaseCacheService } from '@app/data/cache/database-cache.service';

/**
 * Route guard based on functionAttributes.
 *
 * Specify required features in route.data:
 * - requiredFeature: Single feature (checks current section group)
 * - requiredFeatures: Multiple features (any one, checks current section group)
 * - checkAllSectionGroups: If true, checks all section groups (for /mentions)
 *
 * @example Current section group check
 * {
 *   path: 'users/:userId/dialogs',
 *   canActivate: [featureGuard],
 *   data: { requiredFeature: 'Dialog' }
 * }
 *
 * @example All section groups check (for /mentions)
 * {
 *   path: 'mentions',
 *   canActivate: [featureGuard],
 *   data: { requiredFeature: 'Dialog', checkAllSectionGroups: true }
 * }
 */
export const featureGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
): Promise<boolean | UrlTree> => {
  const permission = inject(PermissionService);
  const router = inject(Router);
  const databaseCache = inject(DatabaseCacheService);

  // Single feature
  const requiredFeature = route.data['requiredFeature'] as
    | FeatureAttribute
    | undefined;
  // Multiple features (any one)
  const requiredFeatures = route.data['requiredFeatures'] as
    | FeatureAttribute[]
    | undefined;
  // Check all section groups (for /mentions)
  const checkAllSectionGroups = route.data['checkAllSectionGroups'] as
    | boolean
    | undefined;

  // No requirement specified - allow access
  if (!requiredFeature && !requiredFeatures?.length) {
    return true;
  }

  // Get sectionId from route and ensure section data is loaded
  const sectionId = getSectionIdFromRoute(route);
  if (sectionId) {
    try {
      // Wait for initial data to be loaded first
      await waitForSectionData(databaseCache);
      // Then set the selected section
      await databaseCache.setSelectedSectionById(sectionId);
    } catch (error) {
      console.error('[featureGuard] Failed to load section data', error);
      return router.createUrlTree(['/not-found']);
    }
  }

  // Select check function based on scope
  const checkFeature = checkAllSectionGroups
    ? (f: FeatureAttribute) => permission.hasFeatureInAnySectionGroup(f)
    : (f: FeatureAttribute) => permission.hasFeature(f);

  // Single feature check
  if (requiredFeature && checkFeature(requiredFeature)) {
    return true;
  }

  // Multiple features check (any one)
  if (requiredFeatures?.length && requiredFeatures.some(checkFeature)) {
    return true;
  }

  // Access denied - redirect with reason
  const feature = requiredFeature ?? requiredFeatures?.[0];

  if (sectionId) {
    return router.createUrlTree(['/home', sectionId, 'unauthorized'], {
      queryParams: { reason: 'feature', feature },
    });
  }

  return router.createUrlTree(['/not-found']);
};

/**
 * Wait for section data to be available.
 * This ensures the guard doesn't run before ContractStatusGuard has loaded the data.
 */
async function waitForSectionData(
  databaseCache: DatabaseCacheService,
  timeoutMs = 5000,
): Promise<void> {
  const startTime = Date.now();
  while (!databaseCache.getSectionList()?.length) {
    if (Date.now() - startTime > timeoutMs) {
      throw new Error('[featureGuard] Timeout waiting for section data');
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

/**
 * Get sectionId from route parameters (current or parent routes)
 */
function getSectionIdFromRoute(route: ActivatedRouteSnapshot): string | null {
  // Try current route
  let sectionId = route.paramMap.get('sectionId');

  // Traverse parent routes
  let parent = route.parent;
  while (!sectionId && parent) {
    sectionId = parent.paramMap.get('sectionId');
    parent = parent.parent;
  }

  return sectionId;
}
